import React, { useEffect, useState } from 'react';
import { FormattedNumber } from 'react-intl';

import { CMSHeading } from '@swf/cms';
import CloseButton, { Variant } from '@swf/components/StarsModal/components/CloseButton';
import { fetchJson } from '@swf/core/utils/api';

import styles from '../../Events.scss';

export interface PayoutsInfo {
  payoutsUrl: string;
  fullInfo?: boolean;
  onClose: () => void;
}

export interface Payouts {
  position: number;
  percentage: number;
  tickets: number;
  value: number;
  country: string;
  player: string;
}

interface PayoutsData {
  list: [
    {
      position: number;
      percentage: number;
      tickets: number;
      value: number;
      winner: {
        country: string;
        player: string;
      };
    }
  ];
}

const extractPayouts = (data: PayoutsData) =>
  data?.list?.map((item) => ({
    position: item.position,
    percentage: item.percentage,
    tickets: item.tickets,
    value: item.value,
    country: item.winner.country,
    player: item.winner.player
  })) as unknown as Payouts[];

const EventPayouts: React.FC<PayoutsInfo> = ({ payoutsUrl, onClose, fullInfo = false }) => {
  const [payouts, setPayouts] = useState<Payouts[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchJson<PayoutsData>(payoutsUrl, { signal });
        setPayouts(extractPayouts(data));
      } catch (err) {
        setPayouts([]);
      }
    };

    const abortController = new AbortController();
    const { signal } = abortController;

    fetchData();

    return () => abortController.abort();
  }, [payoutsUrl]);

  const title = fullInfo ? 'Winners' : 'Prizes';

  return (
    <div className={styles.eventInfo}>
      <CMSHeading title={title} level="h3" size="l" hero={false} alignment={null} target={null} />
      <CloseButton variant={Variant.Dark} onClick={onClose} />
      <div className={styles.tableContainer}>
        <table className={styles.eventsTable}>
          <thead>
            <tr>
              <th>Position</th>
              <th>Prize</th>
              {fullInfo && (
                <>
                  <th>Player</th>
                  <th>Country</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {payouts.map((item, index) => (
              <tr key={index}>
                <td>{item.position}</td>
                <td>
                  <FormattedNumber style="decimal" value={item.value} />
                </td>
                {fullInfo && (
                  <>
                    <td>{item.player}</td>
                    <td>{item.country}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EventPayouts;
